import React, { useState, useEffect } from "react";
import { 
  Loader2, 
  AlertCircle,
  Calendar,
  Users,
  Search,
  MapPin,
  CalendarDays,
  CircleDot,
  ArrowRight
} from 'lucide-react';
import { fetchWithAuth, SEASON_EVENTS_API, REGISTRATIONS_API } from "../../utils/api";
import { useTheme } from "../../context/ThemeContext";

// --- Registrations Modal Component ---
const RegistrationsModal = ({ isOpen, onClose, registrations = [], eventData }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className={`card w-[800px] shadow-xl ${
        isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : 'bg-base-100'
      }`}>
        <div className="card-body">
          <h2 className="card-title">Registrations for {eventData?.name} ({eventData?.city})</h2>
          <p className="text-sm text-base-content/70 mb-4">
            Event Dates: {eventData?.startDate} - {eventData?.endDate}
          </p>

          {registrations.length === 0 ? (
            <div className="alert alert-info">
              <span>No registrations found for this event.</span>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[400px] border">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>User ID</th>
                    <th>Dancers</th>
                    <th>Teachers</th>
                    <th>Routines</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((reg) => (
                    <tr key={reg.registrationId}>
                      <td>{reg.userId}</td>
                      <td>
                        {Array.isArray(reg.dancers) ? reg.dancers.length : 0}
                      </td>
                      <td>
                        {Array.isArray(reg.teachers) ? reg.teachers.length : 0}
                      </td>
                      <td>
                        {
                          Array.isArray(reg.competition?.routines)
                            ? reg.competition.routines.length
                            : 0
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-end mt-4">
            <button onClick={onClose} className="btn btn-primary">
              Close
            </button>
          </div>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onClose}></div>
    </div>
  );
};

// --- Main EventsManager Page ---
export default function EventsManager() {
  const [events, setEvents] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState("all");
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  // For viewing registrations:
  const [registrations, setRegistrations] = useState([]);
  const [isRegModalOpen, setIsRegModalOpen] = useState(false);
  const [eventDataForRegs, setEventDataForRegs] = useState(null);

  // Fetch events from season events API on component mount
  useEffect(() => {
    fetchEvents();
  }, []);

  // Fetch Events from Season Events API
  const fetchEvents = async () => {
    try {
      setIsLoading(true);
      
      // Fetch data from the season events API
      const response = await fetchWithAuth(SEASON_EVENTS_API);
      
      if (!response) {
        throw new Error("No data received from API");
      }

      // Set seasons and events data
      setSeasons(response.seasons || []);
      setEvents(response.events || []);
      
      // If there are seasons, set the first one as selected by default
      if (response.seasons && response.seasons.length > 0) {
        setSelectedSeason(response.seasons[0].name);
      }
    } catch (err) {
      console.error("Error fetching events:", err);
      setError(err.message || "Failed to fetch events. Please try again.");
      setEvents([]);
      setSeasons([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // --- View Registrations for an Event ---
  const handleViewRegistrations = async (eventItem) => {
    try {
      // fetch from registrations API with eventId parameter
      const data = await fetchWithAuth(
        `${REGISTRATIONS_API}?eventId=${eventItem.eventId}`
      );

      setRegistrations(data.registrations || []);
      setEventDataForRegs(eventItem);
      setIsRegModalOpen(true);
    } catch (error) {
      console.error("Error fetching registrations:", error);
      setRegistrations([]);
      setEventDataForRegs(eventItem);
      setIsRegModalOpen(true);
    }
  };

  // Filter Events by search query and selected season
  const filteredEvents = events.filter((evt) => {
    const matchesSearch = searchQuery.trim() === "" ||
      evt.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      evt.city.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesSeason = selectedSeason === "all" || evt.season === selectedSeason;
    
    return matchesSearch && matchesSeason;
  });

  // Sort events by date (most recent first)
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    const dateA = new Date(a.startDate);
    const dateB = new Date(b.startDate);
    return dateB - dateA;
  });

  // Status Badge Styling
  const getEventTypeClass = (eventType) => {
    if (!eventType) return "badge badge-ghost";
    
    // Simple hash of the string to generate a consistent color
    const hash = eventType.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + acc;
    }, 0);
    
    const badges = ["badge-primary", "badge-secondary", "badge-accent", "badge-info"];
    return `badge ${badges[hash % badges.length]}`;
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="alert alert-error shadow-lg mb-4">
          <div>
            <AlertCircle className="stroke-current flex-shrink-0 h-6 w-6" />
            <span>{error}</span>
          </div>
        </div>

        <div className="flex justify-center my-8">
          <button onClick={() => fetchEvents()} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto p-6 space-y-8">
        {/* Header */}
        <div className={`rounded-box p-6 ${
          isDarkMode 
            ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5 shadow-xl' 
            : 'bg-base-100 border border-base-300'
        }`}>
          <div className="flex flex-col lg:flex-row justify-between gap-6">
          <div>
              <h1 className="text-4xl font-bold text-base-content">Events Overview</h1>
              <p className="text-base-content/60 mt-2">
                View and manage all events from your seasons
            </p>
            </div>
            <div className="flex items-center">
              <button 
                onClick={fetchEvents} 
                className="btn btn-outline"
              >
                <Loader2 className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className={`card ${
          isDarkMode 
            ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5 shadow-xl' 
            : 'bg-base-100 border border-base-300'
        }`}>
          <div className="card-body p-6">
            <div className="flex flex-col md:flex-row gap-4 justify-between">
              <div className="form-control w-full md:w-64">
                <label className="label">
                  <span className="label-text">Filter by Season</span>
                </label>
                <select 
                  className="select select-bordered w-full" 
                  value={selectedSeason}
                  onChange={(e) => setSelectedSeason(e.target.value)}
                >
                  <option value="all">All Seasons</option>
                  {seasons.map((season, index) => (
                    <option key={index} value={season.name}>
                      {season.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-control w-full md:w-auto flex-1">
              <label className="label">
                  <span className="label-text">Search Events</span>
              </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <Search className="w-4 h-4 text-base-content/50" />
                  </div>
              <input
                type="text"
                    className="input input-bordered w-full pl-10"
                    placeholder="Search by name or city..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Events Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {sortedEvents.length === 0 ? (
            <div className="col-span-full">
              <div className="alert alert-info">
                <AlertCircle className="w-6 h-6" />
                <span>
                  {seasons.length === 0 
                    ? "No seasons or events found. Please create them in Season & Event Settings." 
                    : "No events found matching your search criteria."}
                </span>
              </div>
              </div>
            ) : (
            sortedEvents.map((event) => (
              <div 
                key={event.eventId || `event-${event.name}-${event.startDate}`} 
                className={`card hover:shadow-md transition-shadow ${
                  isDarkMode 
                    ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' 
                    : 'bg-base-100 border border-base-300'
                }`}
              >
                <div className="card-body p-6">
                  <div className="flex justify-between items-start">
                    <h2 className="card-title text-lg">{event.name}</h2>
                    <span className={getEventTypeClass(event.eventType)}>
                      {event.eventType || "Event"}
                    </span>
                  </div>
                  
                  <div className="space-y-3 mt-3">
                    <div className="flex items-center text-base-content/70">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span>{event.city}</span>
                    </div>
                    
                    <div className="flex items-center text-base-content/70">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>{formatDate(event.startDate)} - {formatDate(event.endDate)}</span>
                    </div>
                    
                    <div className="flex items-center text-base-content/70">
                      <CalendarDays className="w-4 h-4 mr-2" />
                      <span>Season: {event.season}</span>
                    </div>
                          </div>
                  
                  <div className="card-actions justify-end mt-4">
                          <button
                      className="btn btn-sm btn-outline"
                      onClick={() => handleViewRegistrations(event)}
                          >
                      <Users className="w-4 h-4 mr-1" />
                            View Registrations
                          </button>
                    
                    <a 
                      href={`/admin/event-detail/${event.eventId}`} 
                      className="btn btn-sm btn-primary"
                          >
                      Details
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </a>
                  </div>
                </div>
              </div>
            ))
            )}
        </div>
        
        {/* Summary Stats */}
        <div className={`stats stats-vertical lg:stats-horizontal shadow w-full ${
          isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : ''
        }`}>
          <div className="stat">
            <div className="stat-title">Total Events</div>
            <div className="stat-value">{events.length}</div>
            <div className="stat-desc">Across all seasons</div>
          </div>
          
          <div className="stat">
            <div className="stat-title">Active Seasons</div>
            <div className="stat-value">{seasons.length}</div>
            <div className="stat-desc">Available for scheduling</div>
          </div>
          
          <div className="stat">
            <div className="stat-title">Upcoming Events</div>
            <div className="stat-value">
              {events.filter(e => new Date(e.startDate) > new Date()).length}
            </div>
            <div className="stat-desc">Future scheduled events</div>
          </div>
        </div>
      </div>

      {/* Registrations Modal */}
      <RegistrationsModal
        isOpen={isRegModalOpen}
        onClose={() => setIsRegModalOpen(false)}
        registrations={registrations}
        eventData={eventDataForRegs}
      />
    </>
  );
}
